import { useDraggable } from "@dnd-kit/core";
import React from "react";

import { formatCurrency } from "../lib/format";
import { contractInAviso, formatContractMonth, FAROL_COLORS } from "../lib/contract";
import { Opportunity } from "../types/crm";

interface OpportunityCardProps {
  opportunity: Opportunity;
}

interface OpportunityCardActions extends OpportunityCardProps {
  onEdit?: (opp: Opportunity) => void;
  onDelete?: (opp: Opportunity) => void;
  canModify?: boolean;
}

export function OpportunityCardContent({ opportunity }: OpportunityCardProps) {
  const farol = opportunity.farol ? FAROL_COLORS[opportunity.farol] : null;
  const contractAviso = opportunity.contract_end && contractInAviso(opportunity.contract_end);

  return (
    <>
      <div className="mb-1 flex flex-wrap gap-1">
        {opportunity.needs_completion && (
          <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
            Incompleto
          </span>
        )}
        {opportunity.result === "ganho" && (
          <span className="inline-block rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-dark">
            Ganho
          </span>
        )}
        {opportunity.result === "perdido" && (
          <span className="inline-block rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">
            Perdido
          </span>
        )}
      </div>
      <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
        {farol && (
          <span
            className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
            style={{ backgroundColor: farol.dot }}
            title={farol.label}
          />
        )}
        {opportunity.company_detail.name}
      </p>
      {opportunity.contract_end && (
        <span
          className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            contractAviso ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#F1F5F9] text-[#64748B]"
          }`}
        >
          🗓 {formatContractMonth(opportunity.contract_end)}
        </span>
      )}
      <p className="text-xs text-slate-500">
        {opportunity.primary_contact_detail?.name ?? "— (a preencher)"}
      </p>
      <p className="mt-2 text-sm font-medium text-slate-700">
        {formatCurrency(opportunity.mrr_estimated)}
      </p>
      <div className="mt-2 flex flex-wrap gap-1">
        {opportunity.products_detail.map((product) => (
          <span
            key={product.id}
            className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary"
          >
            {product.name}
          </span>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-400">
        {opportunity.owner_detail.username}
        {opportunity.channel_detail ? ` · ${opportunity.channel_detail.name}` : ""}
      </p>
    </>
  );
}

export function OpportunityCard({ opportunity, onEdit, onDelete, canModify }: OpportunityCardActions) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: opportunity.id,
  });

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  // Borda esquerda por prioridade: farol vermelho > etapa de alerta > padrão.
  const leftBorder =
    opportunity.farol === "vermelho"
      ? "#EF4444"
      : opportunity.stage_detail.is_alert
      ? "#F59E0B"
      : "#5C3F5C";

  // Impede que clicar nos botões inicie o drag do card.
  const stop = (e: React.PointerEvent | React.MouseEvent) => e.stopPropagation();

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, borderLeft: `3px solid ${leftBorder}` }}
      {...listeners}
      {...attributes}
      data-testid={`opportunity-card-${opportunity.id}`}
      className={`group cursor-grab touch-none rounded-[10px] bg-surface p-3 shadow-sm ${
        opportunity.needs_completion
          ? "border border-dashed border-amber-400"
          : "border border-border-subtle"
      } ${isDragging ? "opacity-40" : ""}`}
    >
      <OpportunityCardContent opportunity={opportunity} />
      {canModify && (onEdit || onDelete) && (
        <div className="mt-2 flex gap-3 border-t border-border-subtle pt-2 opacity-0 transition-opacity group-hover:opacity-100">
          {onEdit && (
            <button
              type="button"
              onPointerDown={stop}
              onClick={(e) => {
                stop(e);
                onEdit(opportunity);
              }}
              className="text-xs font-medium text-primary hover:underline"
            >
              Editar
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onPointerDown={stop}
              onClick={(e) => {
                stop(e);
                onDelete(opportunity);
              }}
              className="text-xs font-medium text-red-600 hover:underline"
            >
              Excluir
            </button>
          )}
        </div>
      )}
    </div>
  );
}
